import { useEffect } from 'react';
import { Checkbox, DatePicker, Form, Input, Modal, Radio, Select, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bulkTransferTeam } from '@/services/teamAssignments.service';
import { FUNCTIONS, personName } from '@/components/team-assignments/model';

/**
 * Hands over everything a person does, in the ticked types of work, to one
 * colleague: when someone leaves or changes jobs. Firms already in a hand-over
 * stay as they are and are counted as skipped.
 */
export default function BulkTransferDialog({ open, from, people, byFn, firms, onClose, onDone }) {
  const { t } = useTranslation('teamAssignments');
  const [form] = Form.useForm();
  const mode = Form.useWatch('mode', form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({ mode: 'transition', functions: FUNCTIONS.filter((f) => byFn?.[f]) });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v) =>
      bulkTransferTeam({
        fromEmployeeId: from._id,
        toEmployeeId: v.to,
        functions: v.functions,
        mode: v.mode,
        until: v.until ? v.until.format('YYYY-MM-DD') : null,
        note: v.note || '',
      }),
    onSuccess: (res) => {
      message.success(t('bulk.done', { moved: res.moved, skipped: res.skipped }));
      onDone?.();
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  return (
    <Modal
      open={open}
      title={t('bulk.title', { name: personName(from), count: firms })}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('bulk.ok')}
      cancelText={t('actions.cancel')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      <p style={{ opacity: 0.7, marginTop: 0 }}>{t('bulk.hint')}</p>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item name="to" label={t('assign.to')} rules={[{ required: true, message: t('assign.pickPerson') }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('assign.pickPerson')}
            options={people.filter((p) => p._id !== from?._id && !p.left).map((p) => ({ value: p._id, label: personName(p) }))}
          />
        </Form.Item>
        <Form.Item name="functions" label={t('cover.functions')} rules={[{ required: true, type: 'array', min: 1, message: t('cover.pickFunctions') }]}>
          <Checkbox.Group>
            {FUNCTIONS.map((f) => (
              <Checkbox key={f} value={f} disabled={!byFn?.[f]}>
                {t(`functions.${f}`)}
                {byFn?.[f] ? ` (${byFn[f]})` : ''}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>
        <Form.Item name="mode" label={t('assign.when')}>
          <Radio.Group>
            <Radio value="transition">{t('assign.modeTransition')}</Radio>
            <Radio value="now">{t('assign.modeNow')}</Radio>
          </Radio.Group>
        </Form.Item>
        {mode === 'transition' && (
          <Form.Item name="until" label={t('assign.until')} extra={t('assign.untilHint')}>
            <DatePicker format="DD.MM.YYYY" style={{ width: 200 }} />
          </Form.Item>
        )}
        <Form.Item name="note" label={t('assign.note')}>
          <Input maxLength={500} placeholder={t('assign.notePlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
